import { ExternalLink, ChevronUp, ChevronDown, Users } from 'lucide-react';
import {
  PillWrapper,
  PILL_BUTTON_ACTIVE,
  PILL_BUTTON_BASE,
  PILL_BUTTON_IDLE,
  PILL_VALUE_IDLE_CLASS,
} from '@ooh/ui/components/filter-panel';
import { Button } from '@ooh/ui/components/button';
import { cn } from '@ooh/ui/lib/utils';
import { isDeadlineEditEligible } from '../lib/formatters';
import type {
  ReservationListItem,
  ReservationListViewLabels,
  ReservationListViewPermissions,
} from './reservation-list-view-types';

// ─── Row actions builder ──────────────────────────────────────
// Always starts with "Otwórz nośnik" (universal, permission-free),
// then "Otwórz ofertę" when offer exists, then a separator, then
// permission-gated transition items.

export type RowAction = {
  label: string;
  action: string;
  href?: string;
  destructive?: boolean;
  separator?: boolean; // render a separator BEFORE this item
};

// eslint-disable-next-line complexity -- business rule fan-out; every status/role combination is a separate branch
export function buildRowActions(
  item: ReservationListItem,
  permissions: ReservationListViewPermissions,
  currentUserId: number,
  labels: ReservationListViewLabels,
): RowAction[] {
  const actions: RowAction[] = [];

  // Universal: navigate to the unit detail
  actions.push({ label: labels.openUnitLabel, action: 'open_unit', href: item.unitHref });

  // Universal when offer exists: navigate to the offer
  if (item.offerId !== null && item.offerHref !== null) {
    actions.push({ label: labels.openOfferLabel, action: 'open_offer', href: item.offerHref });
  }

  // Permission-gated transitions (separator before first gated item)
  const isOwn = item.holderUserId === currentUserId;
  const canAct = isOwn || permissions.canForceRelease;
  let separatorAdded = false;

  const addGated = (action: RowAction) => {
    if (!separatorAdded) {
      actions.push({ ...action, separator: true });
      separatorAdded = true;
    } else {
      actions.push(action);
    }
  };

  // Media-unit-level action, independent of reservation ownership/status.
  if (permissions.canBlockAvailability) {
    addGated({ label: labels.blockAvailabilityLabel, action: 'block_availability' });
  }

  // Permission-only, independent of ownership (CU-86cb7qgc9) — unlike the
  // status-branch actions below, reservation.extend alone is the gate.
  // `?? false`/`?? null` fail closed for callers that predate this field
  // (e.g. apps/mockups) instead of throwing on an undefined permission.
  if (
    (permissions.canExtendExpiry ?? false) &&
    isDeadlineEditEligible(item.status, item.effectiveExpiresAt ?? null)
  ) {
    addGated({ label: labels.extendExpiryLabel ?? '', action: 'expiry_deadline' });
  }

  if (canAct) {
    if (item.status === 'SOFT_RESERVATION') {
      // close_sale is valid from SOFT_RESERVATION per spec (CAS transition path)
      if (permissions.canUpgradeHard)
        addGated({ label: labels.upgradeHardLabel, action: 'upgrade_hard' });
      if (permissions.canCloseSale)
        addGated({ label: labels.closeSaleLabel, action: 'close_sale' });
      if (permissions.canUpdateDates)
        addGated({ label: labels.editDatesLabel, action: 'edit_dates' });
      if (permissions.canCancel)
        addGated({ label: labels.cancelLabel, action: 'cancel', destructive: true });
    } else if (item.status === 'HARD_RESERVATION') {
      if (permissions.canCloseSale)
        addGated({ label: labels.closeSaleLabel, action: 'close_sale' });
      if (permissions.canUpdateDates)
        addGated({ label: labels.editDatesLabel, action: 'edit_dates' });
      if (permissions.canCancel)
        addGated({ label: labels.cancelLabel, action: 'cancel', destructive: true });
    } else if (item.status === 'OPTION') {
      if (permissions.canUpdateDates)
        addGated({ label: labels.editDatesLabel, action: 'edit_dates' });
      if (permissions.canCancel)
        addGated({ label: labels.cancelLabel, action: 'cancel', destructive: true });
    } else if (item.status === 'SOLD') {
      if (permissions.isAdmin && permissions.canUpdateDates)
        addGated({ label: labels.editDatesLabel, action: 'edit_dates' });
      if (permissions.isAdmin && permissions.canCancel)
        addGated({ label: labels.cancelLabel, action: 'cancel', destructive: true });
    }
  }

  if (permissions.isAdmin && permissions.canOverrideStatus)
    addGated({ label: labels.overrideStatusLabel, action: 'override_status' });
  if (permissions.isAdmin && permissions.canForceRelease && !isOwn)
    addGated({ label: labels.forceReleaseLabel, action: 'force_release', destructive: true });

  return actions;
}

// ─── Sort icon ────────────────────────────────────────────────

// Shared class for the sticky right-hand actions column (header and body cells)
export const STICKY_COL_SHADOW =
  'border-l border-border/40 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]';

const WAITING_CHIP_CLASS =
  'inline-flex items-center justify-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-2xs font-medium text-muted-foreground tabular-nums';

/**
 * The "N waiting" cell.
 *
 * When the caller supplies queue data (`onToggle`) this is an expand toggle
 * that reveals the waiting line inline — deliberately IDENTICAL to the unit
 * detail panel's queue affordance (`UnitReservationsTab`), so the count means
 * the same thing and behaves the same way on every surface that shows it.
 * Without queue data it stays the original static chip.
 */
export function WaitingCountCell({
  item,
  label,
  isOpen,
  onToggle,
}: {
  item: ReservationListItem;
  label: string;
  isOpen: boolean;
  onToggle?: () => void;
}) {
  if (onToggle === undefined) {
    return (
      <span
        data-testid={`reservation-waiting-count-${item.id}`}
        className={WAITING_CHIP_CLASS}
        title={label}
      >
        <Users className="h-3 w-3" />
        {item.waitingCount}
      </span>
    );
  }

  return (
    <Button
      variant="outline"
      size="xs"
      className="gap-1"
      data-testid={`reservation-waiting-count-${item.id}`}
      aria-expanded={isOpen}
      aria-label={label}
      title={label}
      onClick={onToggle}
    >
      <Users className="h-3 w-3" />
      <span className="tabular-nums">{item.waitingCount}</span>
      {isOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
    </Button>
  );
}

/** Pinned one-click scope toggle in the filter bar (e.g. "Moje rezerwacje", "Tylko z kolejką"). */
export function ScopeTogglePill({
  label,
  active,
  testId,
  onToggle,
}: {
  label: string;
  active: boolean;
  testId: string;
  onToggle: () => void;
}) {
  return (
    <PillWrapper label={label} showLabel={false}>
      {}
      <button
        type="button"
        data-testid={testId}
        aria-pressed={active}
        onClick={onToggle}
        className={cn(PILL_BUTTON_BASE, active ? PILL_BUTTON_ACTIVE : PILL_BUTTON_IDLE)}
      >
        <span
          className={cn(
            PILL_VALUE_IDLE_CLASS,
            active ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {label}
        </span>
      </button>
    </PillWrapper>
  );
}

/**
 * Holder cell. Shows the salesperson ("handlowiec") on the primary line with
 * the reservation's rezerwujący underneath when the caller resolved one — the two
 * are different roles that can disagree, and the waitlist surfaces need both.
 * Falls back to the original single-line holder rendering otherwise.
 */
export function HolderCell({
  item,
  labels,
}: {
  item: ReservationListItem;
  labels: ReservationListViewLabels;
}) {
  if (item.salespersonName == null) {
    return <span className="text-[13px]">{item.holderUserName}</span>;
  }
  return (
    <>
      <p className="truncate text-[13px]">{item.salespersonName}</p>
      <p className="truncate text-2xs text-muted-foreground">
        {labels.holderInlineLabel ?? labels.holderLabel}: {item.holderUserName}
      </p>
    </>
  );
}

/**
 * Offer cell. Leads with the client when resolved (that is who an operator
 * scanning the list is looking for), with the offer link demoted underneath;
 * otherwise keeps the original offer-only rendering.
 */
export function OfferCell({
  item,
  labels,
}: {
  item: ReservationListItem;
  labels: ReservationListViewLabels;
}) {
  const hasClient = item.clientName != null;
  const offerSize = hasClient ? 'text-2xs' : 'text-[13px]';

  return (
    <>
      {hasClient ? <p className="max-w-[170px] truncate text-[13px]">{item.clientName}</p> : null}
      {item.offerName !== null && item.offerHref !== null ? (
        <a
          href={item.offerHref}
          className={cn(
            'group flex max-w-[170px] items-center gap-1 text-primary hover:text-primary/80',
            offerSize,
          )}
        >
          <span className="truncate">{item.offerName}</span>
          <ExternalLink className="h-3 w-3 shrink-0 opacity-60 group-hover:opacity-100" />
        </a>
      ) : (
        <span className={cn('text-muted-foreground', offerSize)}>{labels.noOfferLabel}</span>
      )}
    </>
  );
}

export function SortIcon({ active, dir }: { active: boolean; dir: 'asc' | 'desc' }) {
  if (!active) return null;
  return (
    <span className="inline-block ml-1 text-2xs leading-none">{dir === 'asc' ? '↑' : '↓'}</span>
  );
}
